import { Paper, styled } from "@mui/material";

import { useMeasureConfigs } from "hooks/measureConfigHooks";
import { useStudyArea, useStudyAreaResult } from "hooks/studyAreaHooks";

import { CostBenefitsGraphs } from "components/result/CostBenefitsGraphs";
import { CostBenefitsShare } from "components/result/CostBenefitsShare";
import { Details } from "components/result/Details";
import { LeafletMap } from "components/result/LeafletMap";
import { ThemeGraphs } from "components/result/ThemeGraphs";
import { TotalResult } from "components/result/TotalResult";

interface Props {
  uuid: string;
}

export function ResultPageLoader(props: Props) {
  const { uuid } = props;
  const { data } = useStudyAreaResult(uuid);
  const { data: studyArea } = useStudyArea(uuid);
  const { data: measureConfigs } = useMeasureConfigs(uuid);
  return (
    <StyledPaper elevation={0}>
      {data && studyArea && (
        <TotalResult
          score={data.score}
          className="total-score-grid"
          elevation={0}
          data-tour="step-4"
          computationDate={studyArea?.computationDate}
          computationVersion={studyArea?.computationVersion}
        />
      )}
      {data && (
        <ThemeGraphs
          elevation={0}
          className="theme-graph-grid"
          themeScores={data.themeScores}
        />
      )}
      {studyArea && measureConfigs && (
        <LeafletMap
          elevation={0}
          className="leaflet-map-grid"
          studyArea={studyArea}
          uuid={uuid}
          measureConfigs={measureConfigs}
        />
      )}
      {/* <div className="empty" /> */}
      <div className="side-section">
        {studyArea && (
          <CostBenefitsShare
            className="share-grid"
            elevation={0}
            uuid={uuid}
            readOnlyUuid={studyArea.readOnlyUuid}
            isReadOnly={studyArea.readOnly}
          />
        )}
        {data && studyArea && (
          <Details
            className="detail-grid"
            elevation={0}
            studyArea={studyArea}
            adaptationIndicatorValues={data.adaptationIndicatorValues}
          />
        )}
        {data && (
          <CostBenefitsGraphs
            className="cost-benefit-grid"
            elevation={0}
            costs={data.costs}
            coBenefits={data.coBenefits}
          />
        )}
      </div>
    </StyledPaper>
  );
}

const StyledPaper = styled(Paper)`
  background-color: transparent;
  height: 100%;
  flex-grow: 1;
  display: grid;
  gap: ${({ theme }) => theme.spacing(8)};
  margin-left: 10%;
  margin-right: 10%;
  margin-top: 2%;
  margin-bottom: 2%;
  column-gap: ${({ theme }) => theme.spacing(15)};
  grid-template-areas:
    "totalScoreSection totalScoreSection"
    "themeGraphSection sideSection"
    "leafletMapSection sideSection";

  grid-template-rows: minmax(0, auto) minmax(500px, 520px) minmax(800px, 1fr);
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);

  [class*="-grid"] {
    background-color: transparent;
  }

  .total-score-grid {
    grid-area: totalScoreSection;
  }
  .share-grid {
    grid-area: shareSection;
  }
  .theme-graph-grid {
    grid-area: themeGraphSection;
    margin-bottom: ${({ theme }) => theme.spacing(5)};
  }
  .cost-benefit-grid {
    grid-area: costBenefitsSection;
  }

  .detail-grid {
    grid-area: detailSection;
  }
  .leaflet-map-grid {
    grid-area: leafletMapSection;
  }

  .empty {
    grid-area: empty;
  }

  .side-section {
    grid-area: sideSection;
    display: grid;
    grid-template-areas:
      "shareSection"
      "detailSection"
      "costBenefitsSection";
    gap: ${({ theme }) => theme.spacing(8)};
    grid-template-rows: minmax(0, auto) minmax(0, auto) minmax(600px, 1fr);
    grid-template-columns: minmax(0, 1fr);
  }
`;
